import { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { HelmetProvider, Helmet } from "react-helmet-async";

import { AuthProvider, useAuth } from "./context/AuthContext";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";

import Home from "./pages/Home";
import Movies from "./pages/Movies";
import TvShows from "./pages/TvShows";
import Details from "./pages/Details";
import Player from "./pages/Player";
import LiveTv from "./pages/LiveTv";
import Search from "./pages/Search";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Watchlist from "./pages/Watchlist";
import Feedback from "./pages/Feedback";
import StaticPage from "./pages/StaticPage";
import ComingSoon from "./pages/ComingSoon";
import CategoryPage from "./pages/CategoryPage";

// Protected route wrapper — redirects to login if not authenticated
function ProtectedRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

// Guest route — redirects to home if already logged in
function GuestRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? <Navigate to="/" replace /> : children;
}

// Layout wrapper — includes Navbar + Footer (hidden on player page)
function Layout({ children, settings }) {
  return (
    <>
      <Navbar settings={settings} />
      <main className="min-h-screen">{children}</main>
      <Footer settings={settings} />
    </>
  );
}

function AppRoutes() {
  const [settings, setSettings] = useState({
    site_name: "The Slay Network",
    site_description: "The Slay Network brings you a vast library of free LGTBQIA movies, tv shows, and live TV content.",
    playstore_url: "https://play.google.com/store/apps/details?id=com.watchnownetwork.theslaynetwork",
    logo_url: null, // Defaults to /images/large_icon.png in components if null
    social_links: {
      instagram: "https://www.instagram.com/theslaynetwork/",
      facebook: "https://www.facebook.com/theslaynetwork",
      twitter: "https://x.com/theslaynetwork",
      // tiktok: "",
      // youtube: ""
    },
  });

  return (
    <>
      <Helmet>
        {settings?.site_name && <title>{settings.site_name}</title>}
        {settings?.site_description && <meta name="description" content={settings.site_description} />}
        {settings?.site_name && <meta property="og:site_name" content={settings.site_name} />}
        {/* You can add more global defaults here */}
      </Helmet>
      <Routes>
      {/* ── Player (no navbar/footer) ── */}
      <Route path="/player/:type/:id" element={<Player />} />

      {/* ── Live TV (modal overlay, no layout) ── */}
      <Route path="/live-tv" element={<LiveTv />} />

      {/* ── Pages with Navbar + Footer ── */}
      <Route
        path="/"
        element={
          <Layout settings={settings}>
            <Home />
          </Layout>
        }
      />

      <Route
        path="/movies"
        element={
          <Layout settings={settings}>
            <Movies />
          </Layout>
        }
      />

      <Route
        path="/tvshows"
        element={
          <Layout settings={settings}>
            <TvShows />
          </Layout>
        }
      />

      <Route
        path="/category"
        element={
          <Layout settings={settings}>
            <CategoryPage />
          </Layout>
        }
      />

      <Route
        path="/details/:type/:id"
        element={
          <Layout settings={settings}>
            <Details />
          </Layout>
        }
      />

      <Route
        path="/search"
        element={
          <Layout settings={settings}>
            <Search />
          </Layout>
        }
      />

      {/* ── Auth (Guest only) ── */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <Layout settings={settings}>
              <Login />
            </Layout>
          </GuestRoute>
        }
      />

      <Route
        path="/register"
        element={
          <GuestRoute>
            <Layout settings={settings}>
              <Register />
            </Layout>
          </GuestRoute>
        }
      />

      {/* ── Protected (logged in only) ── */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout settings={settings}>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <Layout settings={settings}>
              <Watchlist />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Public Pages ── */}
      <Route
        path="/feedback"
        element={
          <Layout settings={settings}>
            <Feedback />
          </Layout>
        }
      />

      <Route
        path="/privacy-policy"
        element={
          <Layout settings={settings}>
            <StaticPage pageKey="privacy-policy" />
          </Layout>
        }
      />

      <Route
        path="/terms-of-service"
        element={
          <Layout settings={settings}>
            <StaticPage pageKey="terms-of-service" />
          </Layout>
        }
      />

      <Route
        path="/copyright"
        element={
          <Layout settings={settings}>
            <StaticPage pageKey="copyright" />
          </Layout>
        }
      />


      <Route
        path="/advertising-policy"
        element={
          <Layout settings={settings}>
            <StaticPage pageKey="advertising-policy" />
          </Layout>
        }
      />

      <Route
        path="/cookies-policy"
        element={
          <Layout settings={settings}>
            <StaticPage pageKey="cookies-policy" />
          </Layout>
        }
      />

      {/* ── Catch-all ── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <AppRoutes />
          <CookieConsent />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover
            theme="dark"
          />
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}
